import { useContext } from 'react'

import {
  Box, Button, Form, Image, Link, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useAgencyMarketing from '@hooks/useAgencyMarketing'
import useProviders from '@hooks/useProviders'

import EntitySelectBox from '@components/EntitySelectBox'

import ProductSelection from './components/ProductSelection'
import ProviderTiles from './components/ProviderTiles'
import useAgencyMarketingOrder from './hooks/useAgencyMarketingOrder'

const MODAL_KEY = 'CreateAgencyMarketingOrderModal'

type ModalCallbacks = {
  closeModal: () => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateAgencyMarketingOrderModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { orderPayload } = modalPayload
  const { categoryId, serviceProductId, templatePage } = orderPayload

  const {
    permission_ids,
    preview_image,
    title: templatePageTitle,
  } = templatePage

  const isCanvaTemplate = templatePage?.builder === 'canva' || Number(templatePage?.builder) === 1

  const {
    callbacks: { addAgencyMarketingCampaign },
  } = useAgencyMarketing()

  const agencyMarketingOrderPayload = useAgencyMarketingOrder({ serviceProductId })
  const { agencyMarketingOrderFormPayload } = agencyMarketingOrderPayload

  const {
    entityState,
    handlers,
    saveEnabled,
  } = agencyMarketingOrderFormPayload

  const {
    selectedAgencyId,
    selectedProviderId,
    selectedProviderPriceListItemOptions,
    selectedProviderPriceListItemId,
  } = entityState

  const supportedProviderIds = digObject(permission_ids, 'providers', [])

  const providersPayload = useProviders({
    performHttpRequests: true,
    filters: {
      limit: 300,
    },
    requestOptions: {
      include_provider_alternate_logo: true,
    },
  })
  const { filteredProviders } = providersPayload

  const supportedProviders = filteredProviders.filter(
    provider => supportedProviderIds?.includes(provider.id),
  )

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Add to" titleSecondLine="Campaign" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label={templatePageTitle}>
            <Box variant="whiteGrey">
              <Link href={preview_image} target="_blank" textProps={{ width: '100%', height: '100%' }}>
                <Image
                  backgroundSize="contain"
                  borderRadius={5}
                  forwardProps={{ position: 'relative' }}
                  height={240}
                  boxProps={{ marginTop: '20px', marginBottom: '20px' }}
                  // @ts-expect-error TS2322
                  placeHolder
                  url={preview_image}
                />
              </Link>
            </Box>
          </Form.Field>

          <Form.Field label="* Select Agency" marginTop="large">
            <EntitySelectBox
              data-value-type="number"
              entityKey="agencies"
              // @ts-expect-error TS2322
              name="selectedAgencyId"
              value={selectedAgencyId}
              {...handlers}
            />
          </Form.Field>

          {!!selectedAgencyId && (
            <ProviderTiles formPayload={agencyMarketingOrderFormPayload} providers={supportedProviders} />
          )}

          {!!selectedAgencyId && !!selectedProviderId && (
            <>
              <SectionDivider marginTop="medium">
                Select Product & Options
              </SectionDivider>

              <ProductSelection agencyMarketingOrderPayload={agencyMarketingOrderPayload} />
            </>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          onClick={() => addAgencyMarketingCampaign({
            categoryId,
            isCanvaTemplate,
            // @ts-expect-error TS2561
            selectedAgencyId,
            selectedProviderPriceListItemId,
            selectedProviderPriceListItemOptions,
            selectedTemplatePageId: templatePage?.id,
          })}
          size="large"
        >
          Add to Campaign
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateAgencyMarketingOrderModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
